import * as React from "react";
import { Command } from "../../../schema/types/command";
import { linksOf, request } from "../../../services/cpln";
import { COMMANDS_INTERVAL_MS } from "../../../envVariables";

interface CommandsContextType {
  commands: Command[];
  fetchCommandsPage: (forceClean?: boolean) => Promise<void>;
  fetchCommandsQuery: (search: string) => Promise<void>;
  startFetchCommandsInterval: () => void;
  nextLink: undefined | string;
  error: string;
  isLoading: boolean;
}

export const CommandsContext = React.createContext<CommandsContextType>(null as any);

export const useInitCommandsContext = (itemLink: string) => {
  const [commands, setCommands] = React.useState<Command[]>([]);
  const [commandsLoading, setCommandsLoading] = React.useState<boolean>(false);
  const [commandsError, setCommandsError] = React.useState<string>("");
  const [commandsNextLink, setCommandsNextLink] = React.useState<undefined | string>(undefined);

  async function fetchCommandsPage(forceClean = false) {
    try {
      setCommandsLoading(true);
      setCommandsError("");
      const defaultLink = `${itemLink}/-command`;
      let link = defaultLink;
      if (!forceClean && commandsNextLink) {
        link = commandsNextLink;
      }
      const { data } = await request({ url: link });
      const _commands = forceClean ? data.items : commands.concat(data.items);
      setCommands(_commands);
      setCommandsNextLink(linksOf(data).next);
      setCommandsLoading(false);
    } catch (e) {
      setCommandsLoading(false);
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      setCommands([]);
      setCommandsError(errorMessage);
    }
  }

  async function fetchCommandsQuery(search: string) {
    try {
      setCommandsLoading(true);
      setCommandsError("");
      const link = `${itemLink}/-command/-query`;
      // Search across multiple fields: id, type, lifecycleStage, and tag:name
      const body = {
        kind: "command",
        spec: {
          match: "any",
          terms: [
            { op: "~", property: "id", value: search },
            { op: "~", property: "type", value: search },
            { op: "~", property: "lifecycleStage", value: search },
            { op: "~", tag: "name", value: search },
          ],
        },
      };
      const { data } = await request({ url: link, method: "post", body: body });
      setCommands(data.items);
      setCommandsNextLink(linksOf(data).next);
      setCommandsLoading(false);
    } catch (e) {
      setCommandsLoading(false);
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      setCommands([]);
      setCommandsError(errorMessage);
    }
  }

  const timeoutRef = React.useRef<null | any>(null);

  async function fetchCommandsInterval() {
    await fetchCommandsPage(true);
    timeoutRef.current = setTimeout(() => fetchCommandsInterval(), COMMANDS_INTERVAL_MS);
  }

  const startFetchCommandsInterval = React.useCallback(() => {
    if (!timeoutRef.current) {
      fetchCommandsInterval();
    }
  }, [fetchCommandsInterval]);

  const stopFetchCommandsInterval = React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    return () => stopFetchCommandsInterval();
  }, [stopFetchCommandsInterval]);

  return {
    commands: commands,
    commandsLoading: commandsLoading,
    commandsError: commandsError,
    commandsNextLink: commandsNextLink,
    fetchCommandsPage: fetchCommandsPage,
    fetchCommandsQuery: fetchCommandsQuery,
    startFetchCommandsInterval: startFetchCommandsInterval,
  };
};
